import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable ,  ReplaySubject,  Subject } from 'rxjs';
import { AbstractCRUD, CRUDService, ExceptionService, PaginatedList, UtilsService } from '../../xform-compat';
import { IntradaySummary, Portfolio, Position, PositionAudit, PositionDelta, ReturnPositionDelta } from '../../shared/models';
import { MarginSummaryList } from '../../shared/models/margin-summary.model';
import { catchError, tap } from 'rxjs/operators';


/**
 * Singleton service to interact with portfolio API
 */

@Injectable({
  providedIn: 'root'
})
export class PortfolioService extends AbstractCRUD<Portfolio> implements CRUDService<Portfolio> {
  private portfolioUpdate: Subject<any>;
  private portfolios: ReplaySubject<PaginatedList<Portfolio>>;
  constructor(
    exceptionService: ExceptionService,
    http: HttpClient,
    utilsService: UtilsService,
  ) {
    super(exceptionService, http, utilsService);
    this.portfolioUpdate = new Subject<any>();
    this.portfolios = new ReplaySubject<PaginatedList<Portfolio>>(1);
  }

  /**
   * Used to get the history of the main portfolio
   */
  getResourcePath(): string {
    return `/portfolio/portfolios/1/history`;
  }

  /**
   * Sends a message to the subscribed object on the component
   * @param message
   */
  public sendMessage(message: string) {
    this.portfolioUpdate.next({ text: message });
  }

  /**
   * Observable to listen when a message is sent
   */
  public getMessage(): Observable<any> {
    return this.portfolioUpdate.asObservable();
  }

  /**
   * Observable to listen when the porfolios are loaded
   */
   public getCurrentPortfolios(): Observable<PaginatedList<Portfolio>> {
    return this.portfolios.asObservable();
  }

  /**
   * Get the list of exchange rates.
   */
  public getExchangeRates(): Observable<any> {
    return super.getHTTP()
      .get(`/api/rms/rates`)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  /**
   * Get the list of portfolio records.
   */
  public getPortfolios(): Observable<PaginatedList<Portfolio>> {
    return super.getHTTP()
      .get(`/api/portfolio/portfolios`)
      .pipe(
        tap(
          data => this.portfolios.next(data as PaginatedList<Portfolio>)
        ),
        catchError(ex => super.getException().catchBadResponse(ex))
      );
  }

  /**
   * Get the list of portfolio records.
   */
   public getPortfolio(id: number): Observable<Portfolio> {
    return super.getHTTP()
      .get(`/api/portfolio/portfolios/${id}`)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  /**
   * Gets the portfolio history of any given portfolio
   * @param id
   * @param params
   */
  public getPortfolioHistory(id: number, params?: any): Observable<PaginatedList<Portfolio>> {
    const httpParams: HttpParams = params;
    return super.getHTTP()
      .get(`/api/portfolio/portfolios/${id}/history`, { params: httpParams })
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  /**
   * Get the result from the table portfolio_intraday_summary for a given portfolio. Represents that summary calculated aggregations
   * @param id
   */
  public getPortfolioIntradaySummaries(id: number): Observable<PaginatedList<IntradaySummary>> {
    return super.getHTTP()
      .get(`/api/portfolio/portfolios/${id}/intradaysummaries`)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  /**
   * Gets the latest prime-broker margin snapshot for all companies. Firm-wide rather than
   * per-portfolio: the source table has no portfolio dimension.
   */
  public getMarginSummaries(): Observable<MarginSummaryList> {
    return super.getHTTP()
      .get<MarginSummaryList>('/api/portfolio/margin-summary')
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  /**
   * Adds a new position
   * @param id
   * @param body
   */
  public addPosition(id: number, body: Position): Observable<null> {
    return super.getHTTP().post(`/api/portfolio/portfolios/${id}/positions`, body)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  /**
   * Updates an existing position
   * @param portfolioId
   * @param positionId
   * @param body
   */
  public updatePosition(portfolioId: number, positionId: number, body: Position): Observable<null> {
    return super.getHTTP().put(`/api/portfolio/portfolios/${portfolioId}/positions/${positionId}`, body)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  /**
   * Deletes an existing position
   * @param portfolioId
   * @param positionId
   */
  public deactivatePortfolioPosition(portfolioId: number, positionId: number): Observable<null> {
    return super.getHTTP().post(`/api/portfolio/portfolios/${portfolioId}/positions/${positionId}/deactivate`, {})
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }


  /**
   * Get all the portfolio positions
   */
  public getPortfolioPositions(id: number, params: any): Observable<PaginatedList<Position>> {
    const httpParams: HttpParams = params;
    return super.getHTTP()
      .get(`/api/portfolio/portfolios/${id}/positions`,
        { params: httpParams })
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  /**
   * @param body
   */
  public addPortfolio(body: Portfolio): Observable<null> {
    return super.getHTTP().post(`/api/portfolio/portfolios`, body)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  /**
   * @param portfolioId
   * @param body
   */
  public updatePortfolio(portfolioId: number, body: Portfolio): Observable<null> {
    return super.getHTTP().put(`/api/portfolio/portfolios/${portfolioId}`, body)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  /**
   * Updates history item
   * @param item
   * @param {Portfolio} body
   * @returns {Observable<null>}
   */
  public updatePortfolioHistory(item: Portfolio, body: Portfolio): Observable<null> {
    return super.getHTTP().put(`/api/portfolio/portfolios/${item.portfolioId}/history/${item.id}`, body)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  /**
   * Reverts the history of the item
   * @param {Portfolio} item
   * @returns {Observable<null>}
   */
  public revertPortfolioHistory(item: Portfolio): Observable<null> {
    return super.getHTTP()
      .post(`/api/portfolio/portfolios/${item.portfolioId}/history/${item.id}/revert`, {})
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  /**
   * Downloads an exported portfolio based on date range
   * @param {number} portfolioId
   * @param params
   * @returns {Observable<Blob>}
   */
  public exportPortfolio(portfolioId: number, params?: any): Observable<Blob> {
    const headers = new HttpHeaders({ 'Accept': 'application/octet-stream' });

    return super.getHTTP()
      .get(`/api/portfolio/portfolios/${portfolioId}/export`, {
        headers, params: params, responseType: 'blob'
      })
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public getPositionDeltas(portfolioId: number): Observable<Array<PositionDelta>> {
    return super.getHTTP()
      .get(`/api/portfolio/portfolios/${portfolioId}/positions/reconcile`)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public postPositionDeltas(portfolioId: number, returnPositionDeltas: Array<ReturnPositionDelta>): Observable<null> {
    return super.getHTTP()
      .post(`/api/portfolio/portfolios/${portfolioId}/positions/reconcile`, returnPositionDeltas)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public getPositionAudit(portfolioId: number, page: number, pagesize: number): Observable<PaginatedList<PositionAudit>> {
    const params: HttpParams = new HttpParams()
      .set('page', String(page))
      .set('pagesize', String(pagesize));
    return super.getHTTP()
      .get(`/api/portfolio/portfolios/${portfolioId}/positions/audits`, { params: params })
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }
}
